import { useEffect, useState } from "react";
import { collection, getDocs, query, orderBy, limit } from "firebase/firestore";
import { db } from "./firebase";
import { X, Calendar, ArrowRight } from "lucide-react";
import { createPortal } from "react-dom";

const TYPE_COLORS = {
  event:        { bg:"#3b82f6", label:"Event" },
  announcement: { bg:"#f59e0b", label:"Announcement" },
  drive:        { bg:"#22c55e", label:"Drive" },
  camp:         { bg:"#a855f7", label:"Camp" },
  urgent:       { bg:"#ef4444", label:"🚨 Urgent" },
};

export default function EventPopup() {
  const [poster,  setPoster]  = useState(null);
  const [visible, setVisible] = useState(false);
  const [closing, setClosing] = useState(false);

  useEffect(() => {
    // Check if already dismissed in this session
    const dismissed = sessionStorage.getItem("htst_popup_dismissed");
    if (dismissed) return;

    async function load() {
      try {
        const snap = await getDocs(
          query(collection(db, "announcements"), orderBy("createdAt", "desc"), limit(1))
        );
        if (!snap.empty) {
          const data = { id: snap.docs[0].id, ...snap.docs[0].data() };
          if (data.active !== false) {
            setPoster(data);
            // Delay popup appearance by 1.5s for better UX
            setTimeout(() => setVisible(true), 1500);
          }
        }
      } catch(e) {
        console.error("EventPopup load error:", e);
      }
    }
    load();
  }, []);

  function handleClose() {
    setClosing(true);
    setTimeout(() => {
      setVisible(false);
      setClosing(false);
      sessionStorage.setItem("htst_popup_dismissed", "true");
    }, 300);
  }

  if (!poster || !visible) return null;
  const tc = TYPE_COLORS[poster.type] || TYPE_COLORS.event;

  return createPortal(
    <>
      <style>{`
        @keyframes popupIn {
          from { opacity:0; transform:scale(0.92) translateY(20px); }
          to   { opacity:1; transform:scale(1)    translateY(0); }
        }
        @keyframes popupOut {
          from { opacity:1; transform:scale(1)    translateY(0); }
          to   { opacity:0; transform:scale(0.92) translateY(20px); }
        }
        @keyframes overlayIn  { from{opacity:0} to{opacity:1} }
        @keyframes overlayOut { from{opacity:1} to{opacity:0} }
        .popup-overlay {
          position:fixed; inset:0; z-index:10000;
          background:rgba(0,0,0,0.85);
          display:flex; align-items:center; justify-content:center;
          padding:20px;
          animation: ${closing ? "overlayOut" : "overlayIn"} 0.3s ease forwards;
          backdrop-filter:blur(6px);
        }
        .popup-card {
          background:#111009;
          border:1px solid rgba(255,255,255,0.1);
          border-radius:20px;
          overflow:hidden;
          width:100%;
          max-width:480px;
          position:relative;
          animation: ${closing ? "popupOut" : "popupIn"} 0.35s cubic-bezier(0.34,1.56,0.64,1) forwards;
          box-shadow:0 32px 80px rgba(0,0,0,0.6);
        }
        .popup-close {
          position:absolute; top:12px; right:12px; z-index:2;
          width:34px; height:34px; border-radius:50%;
          background:rgba(0,0,0,0.6); border:1px solid rgba(255,255,255,0.15);
          display:flex; align-items:center; justify-content:center;
          cursor:pointer; color:#fff; transition:background 0.2s;
        }
        .popup-close:hover { background:rgba(239,68,68,0.4); }
        .popup-img {
          width:100%; max-height:280px; object-fit:cover; display:block;
        }
        .popup-body { padding:20px 22px 24px; }
        .popup-type-badge {
          display:inline-block; font-size:10px; font-weight:800;
          padding:3px 10px; border-radius:50px; color:#fff;
          text-transform:uppercase; letter-spacing:0.8px; margin-bottom:10px;
        }
        .popup-title {
          font-family:'Playfair Display',serif;
          font-size:20px; font-weight:800; color:#fef3e2;
          margin:0 0 8px; line-height:1.3;
        }
        .popup-date {
          display:flex; align-items:center; gap:6px;
          font-size:12px; color:#78716c; margin-bottom:10px;
        }
        .popup-desc {
          font-size:13px; color:#a8a29e; line-height:1.65; margin:0 0 18px;
        }
        .popup-footer {
          display:flex; align-items:center; justify-content:space-between;
          padding-top:14px; border-top:1px solid rgba(255,255,255,0.06);
        }
        .popup-dismiss {
          font-size:12px; color:#52504d; cursor:pointer;
          background:none; border:none; font-family:inherit;
        }
        .popup-dismiss:hover { color:#78716c; }
        .popup-cta {
          display:inline-flex; align-items:center; gap:6px;
          background:#f59e0b; color:#0a0908;
          border:none; border-radius:50px;
          padding:9px 20px; font-size:13px; font-weight:800;
          cursor:pointer; font-family:inherit; transition:all 0.2s;
          text-decoration:none;
        }
        .popup-cta:hover { transform:translateY(-1px); box-shadow:0 6px 20px rgba(245,158,11,0.4); }

        @media(max-width:480px) {
          .popup-card { border-radius:16px; }
          .popup-img  { max-height:220px; }
          .popup-title { font-size:17px; }
        }
      `}</style>

      <div className="popup-overlay" onClick={handleClose}>
        <div className="popup-card" onClick={e => e.stopPropagation()}>

          {/* Close Button */}
          <button className="popup-close" onClick={handleClose}>
            <X size={16}/>
          </button>

          {/* Image */}
          {poster.imageUrl && (
            <img src={poster.imageUrl} alt={poster.title} className="popup-img"/>
          )}

          {/* Body */}
          <div className="popup-body">
            <div className="popup-type-badge" style={{ background: tc.bg }}>
              {tc.label}
            </div>
            <h2 className="popup-title">{poster.title}</h2>
            {poster.date && (
              <div className="popup-date">
                <Calendar size={13} color="#f59e0b"/>
                {poster.date}
              </div>
            )}
            {poster.description && (
              <p className="popup-desc">{poster.description}</p>
            )}
            <div className="popup-footer">
              <button className="popup-dismiss" onClick={handleClose}>
                Don't show again
              </button>
              <button className="popup-cta" onClick={handleClose}>
                Got it <ArrowRight size={14}/>
              </button>
            </div>
          </div>
        </div>
      </div>
    </>,
    document.body
  );
}